/**
 * Hírlevél / e-mail marketing — tiszta segédfüggvények (Jest-tel tesztelve).
 */

/** A publikus feliratkozó végpont által elfogadott források (backend: PUBLIC_SOURCES). */
export const NEWSLETTER_SOURCES = ['popup', 'footer', 'home', 'checkout', 'profile', 'registration'] as const;
export type NewsletterSource = (typeof NEWSLETTER_SOURCES)[number];

export const isNewsletterSource = (value: string): value is NewsletterSource =>
  (NEWSLETTER_SOURCES as readonly string[]).includes(value);

/** „Kovács János" → { last_name: 'Kovács', first_name: 'János' } (magyar sorrend). */
export const splitHungarianName = (name: string): { first_name: string; last_name: string } => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { last_name: parts[0], first_name: parts.slice(1).join(' ') };
};

export interface SubscribePayload {
  email: string;
  first_name: string;
  last_name: string;
  source: NewsletterSource;
}

/** A feliratkozó űrlap adataiból a POST törzs. Ha csak egy „Név" mező van, szétbontjuk. */
export const buildSubscribePayload = (input: {
  email: string;
  source: NewsletterSource;
  name?: string;
  firstName?: string;
  lastName?: string;
}): SubscribePayload => {
  let firstName = (input.firstName ?? '').trim();
  let lastName = (input.lastName ?? '').trim();
  if (!firstName && !lastName && input.name) {
    const split = splitHungarianName(input.name);
    firstName = split.first_name;
    lastName = split.last_name;
  }
  return {
    email: input.email.trim().toLowerCase(),
    first_name: firstName,
    last_name: lastName,
    source: input.source,
  };
};

export type SubscriberStatus = 'active' | 'pending' | 'unsubscribed' | 'unconfirmed';
type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';

export const SUBSCRIBER_STATUS_META: Record<SubscriberStatus, { label: string; color: ChipColor }> = {
  active: { label: 'Aktív', color: 'success' },
  pending: { label: 'Megerősítésre vár', color: 'warning' },
  unsubscribed: { label: 'Leiratkozott', color: 'default' },
  unconfirmed: { label: 'Megerősítetlen (régi)', color: 'info' },
};

export type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';

export const CAMPAIGN_STATUS_META: Record<CampaignStatus, { label: string; color: ChipColor }> = {
  draft: { label: 'Piszkozat', color: 'default' },
  scheduled: { label: 'Időzített', color: 'info' },
  sending: { label: 'Küldés alatt', color: 'warning' },
  sent: { label: 'Elküldve', color: 'success' },
  failed: { label: 'Sikertelen', color: 'error' },
};

export const isCampaignEditable = (status: CampaignStatus): boolean => status === 'draft' || status === 'scheduled';

/** Vesszővel elválasztott címkék → tisztított, ismétlés nélküli lista (kisbetű-független). */
export const parseTagList = (text: string): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  text.split(',').forEach((raw) => {
    const tag = raw.trim();
    const key = tag.toLowerCase();
    if (tag && !seen.has(key)) {
      seen.add(key);
      result.push(tag);
    }
  });
  return result;
};

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * `<input type="datetime-local">` értéke (helyi idő, időzóna nélkül) → ISO 8601
 * időzóna-eltolással (pl. 2026-10-08T09:30:00+02:00). Érvénytelen bemenetre null.
 */
export const localInputToIso = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? '0'));
  if (Number.isNaN(date.getTime()) || date.getMonth() !== Number(mo) - 1) return null;
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
};

/** ISO időpont → `<input type="datetime-local">` érték (helyi idő, perc pontossággal). */
export const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Százalék két tizedesre, magyar formátumban: 12.5 → „12,50%". */
export const formatRate = (value: number | null | undefined): string =>
  `${(Number(value) || 0).toLocaleString('hu-HU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

/** Arány számítás (0, ha nincs nevező). */
export const computeRate = (numerator: number, denominator: number): number =>
  denominator > 0 ? ((Number(numerator) || 0) / denominator) * 100 : 0;
